import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Cropper from "react-easy-crop";
import { Upload, Trash2, Save, ImageIcon, Images, GripVertical, Crop, X } from "lucide-react";
import { useAuth, API } from "../context/AuthContext";
import { getCroppedBlob } from "../lib/cropImage";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (!url ? "" : url.startsWith("http") ? url : `${BACKEND}${url}`);

const GalleryRow = ({ item, busy, dragging, dragOver, onSaveCaption, onDelete, dnd }) => {
  const [caption, setCaption] = useState(item.caption || "");
  useEffect(() => { setCaption(item.caption || ""); }, [item]);
  const dirty = caption !== (item.caption || "");
  return (
    <div
      draggable={!busy}
      onDragStart={() => dnd.onDragStart(item.id)}
      onDragOver={(e) => { e.preventDefault(); dnd.onDragOver(item.id); }}
      onDrop={(e) => { e.preventDefault(); dnd.onDrop(item.id); }}
      onDragEnd={dnd.onDragEnd}
      data-testid={`gallery-row-${item.id}`}
      className={`border-2 bg-white p-4 flex flex-col sm:flex-row gap-4 items-start transition-colors ${
        dragging ? "opacity-50 border-[#1B6B2E]" : dragOver ? "border-[#F4D03F] bg-[#FEF9E7]" : "border-[#E8F5E9]"
      }`}
    >
      <div className="flex items-center self-stretch sm:self-center text-[#9AA0A6] cursor-grab active:cursor-grabbing" data-testid={`gallery-drag-handle-${item.id}`} title="Sleep om te ordenen">
        <GripVertical size={22} />
      </div>
      <div className="w-full sm:w-48 flex-shrink-0 aspect-[16/9] bg-[#E8F5E9] overflow-hidden">
        <img src={imgSrc(item.url)} alt={item.caption || ""} className="w-full h-full object-cover pointer-events-none" />
      </div>
      <div className="flex-1 w-full space-y-3">
        <div>
          <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Bijschrift (overlay op de homepage)</label>
          <input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            data-testid={`gallery-caption-input-${item.id}`}
            placeholder="Bijv. Vers gemaakte roti met kip"
            className="w-full border-2 border-[#0D3B4C] px-3 py-2 font-[600] focus:border-[#B71C1C] focus:outline-none"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => onSaveCaption(item.id, caption)} disabled={busy || !dirty} data-testid={`gallery-save-caption-${item.id}`}
            className="flex items-center gap-2 bg-[#1B6B2E] text-white font-[700] text-sm px-4 py-2 hover:bg-[#144F22] transition-colors disabled:opacity-40">
            <Save size={15} /> Bijschrift opslaan
          </button>
          <button onClick={() => onDelete(item.id)} disabled={busy} data-testid={`gallery-delete-${item.id}`}
            className="flex items-center gap-2 ml-auto text-[#B71C1C] font-[700] text-sm px-3 py-2 hover:bg-[#FEECEC] transition-colors disabled:opacity-40">
            <Trash2 size={15} /> Verwijderen
          </button>
        </div>
      </div>
    </div>
  );
};

const CropModal = ({ src, caption, onCancel, onConfirm, uploading }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const onComplete = useCallback((_, areaPixels) => setPixels(areaPixels), []);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="crop-modal">
      <div className="absolute inset-0 bg-[#0D3B4C]/70 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white w-full max-w-[640px] shadow-2xl">
        <div className="flex items-center justify-between px-5 h-16 bg-[#FEF9E7] border-b-2 border-[#E8F5E9]">
          <div className="flex items-center gap-2"><Crop size={18} className="text-[#1B6B2E]" /><h3 className="font-[700] text-[#0D3B4C]">Foto bijsnijden (16:9)</h3></div>
          <button onClick={onCancel} aria-label="Sluiten" data-testid="crop-cancel" className="text-[#0D3B4C] hover:text-[#B71C1C]"><X size={22} /></button>
        </div>
        <div className="relative w-full h-[320px] bg-[#0D3B4C]">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={16 / 9}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
          />
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Zoom</label>
            <input type="range" min="1" max="3" step="0.05" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} data-testid="crop-zoom" className="w-full accent-[#1B6B2E]" />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={onCancel} className="font-[700] text-[#0D3B4C] px-4 py-2 hover:bg-[#F1F1F1] transition-colors">Annuleren</button>
            <button onClick={() => onConfirm(pixels)} disabled={uploading || !pixels} data-testid="crop-confirm"
              className="flex items-center gap-2 bg-[#1B6B2E] text-white font-[700] uppercase tracking-wider text-sm px-6 py-3 hover:bg-[#144F22] transition-colors disabled:opacity-40">
              <Upload size={16} /> {uploading ? "Uploaden..." : "Bijsnijden & uploaden"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const GalleryManagement = ({ onAuthError }) => {
  const { authHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [cropState, setCropState] = useState(null); // { src, file }
  const fileRef = useRef(null);

  const dragId = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const err = useCallback((e) => {
    if (e?.response?.status === 401) onAuthError?.();
    else toast.error(e?.response?.data?.detail || "Er ging iets mis.");
  }, [onAuthError]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/gallery`, { headers: authHeader() });
      setItems(data);
    } catch (e) { err(e); } finally { setLoading(false); }
  }, [authHeader, err]);

  useEffect(() => { load(); }, [load]);

  const uploadBlob = async (blob, cap) => {
    const fd = new FormData();
    fd.append("file", blob, "gallery.jpg");
    fd.append("caption", cap || "");
    await axios.post(`${API}/admin/gallery/upload`, fd, { headers: { ...authHeader() } });
  };

  const onFilesSelected = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    if (arr.length === 1) {
      // Single file -> open crop tool
      const reader = new FileReader();
      reader.onload = () => setCropState({ src: reader.result, file: arr[0] });
      reader.readAsDataURL(arr[0]);
    } else {
      // Multiple files -> upload all directly (no crop), captions edited per-row after
      setUploading(true);
      try {
        for (const f of arr) {
          if (!f.type.startsWith("image/")) continue;
          await uploadBlob(f, "");
        }
        toast.success(`${arr.length} foto's toegevoegd aan de galerij.`);
        setCaption("");
        load();
      } catch (e) { err(e); } finally { setUploading(false); }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const confirmCrop = async (pixels) => {
    if (!pixels || !cropState) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(cropState.src, pixels);
      await uploadBlob(blob, caption);
      toast.success("Foto bijgesneden en toegevoegd.");
      setCaption("");
      setCropState(null);
      load();
    } catch (e) { err(e); } finally { setUploading(false); }
  };

  const saveCaption = async (id, cap) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/gallery/${id}`, { caption: cap }, { headers: authHeader() });
      toast.success("Bijschrift opgeslagen.");
      load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Deze foto definitief verwijderen uit de galerij?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { headers: authHeader() });
      toast.success("Foto verwijderd.");
      load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  // Drag & drop reordering
  const dnd = {
    onDragStart: (id) => { dragId.current = id; setDraggingId(id); },
    onDragOver: (id) => { if (id !== dragOverId) setDragOverId(id); },
    onDragEnd: () => { dragId.current = null; setDraggingId(null); setDragOverId(null); },
    onDrop: async (targetId) => {
      const from = dragId.current;
      setDraggingId(null); setDragOverId(null); dragId.current = null;
      if (!from || from === targetId) return;
      const fromIdx = items.findIndex((i) => i.id === from);
      const toIdx = items.findIndex((i) => i.id === targetId);
      if (fromIdx < 0 || toIdx < 0) return;
      const next = [...items];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      setItems(next);
      setBusy(true);
      try {
        await axios.post(`${API}/admin/gallery/reorder`, { ids: next.map((i) => i.id) }, { headers: authHeader() });
        toast.success("Volgorde opgeslagen.");
      } catch (e) { err(e); load(); } finally { setBusy(false); }
    },
  };

  if (loading) return <p className="font-[600] text-[#0D3B4C]">Laden...</p>;

  return (
    <div className="space-y-8" data-testid="gallery-mgmt">
      <div className="flex items-center gap-2">
        <Images size={22} className="text-[#1B6B2E]" />
        <h2 className="font-[700] text-[#0D3B4C]">Homepage Galerij</h2>
      </div>
      <p className="text-sm text-[#0D3B4C] -mt-4">
        Deze foto's verschijnen in de draaiende carousel op de homepage. <b>Sleep</b> de foto's om de volgorde te bepalen; het bijschrift wordt als stijlvolle overlay getoond.
      </p>

      {/* Upload */}
      <div className="bg-[#FEF9E7] p-5" data-testid="gallery-upload-box">
        <h3 className="font-[700] text-[#0D3B4C] mb-3">Nieuwe foto('s) toevoegen</h3>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Bijschrift (optioneel, bij één foto)</label>
            <input value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="gallery-new-caption"
              placeholder="Bijv. Onze specialiteit van het huis"
              className="w-full border-2 border-[#0D3B4C] px-3 py-2 font-[600] focus:border-[#B71C1C] focus:outline-none" />
          </div>
          <label className="flex items-center justify-center gap-2 bg-[#1B6B2E] text-white font-[700] uppercase tracking-wider text-sm px-6 py-3 cursor-pointer hover:bg-[#144F22] transition-colors">
            <Upload size={16} /> {uploading ? "Uploaden..." : "Foto's uploaden"}
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="gallery-upload-input"
              onChange={(e) => onFilesSelected(e.target.files)} />
          </label>
        </div>
        <p className="text-xs text-[#0D3B4C] mt-2">Eén foto → bijsnij-tool (16:9). Meerdere foto's → direct toevoegen (bijschrift per foto daarna).</p>
      </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="border-2 border-dashed border-[#E8F5E9] p-10 text-center" data-testid="gallery-empty">
          <ImageIcon size={36} className="text-[#1B6B2E] mx-auto mb-3" />
          <p className="font-[600] text-[#0D3B4C]">Nog geen foto's. Upload je eerste foto hierboven.</p>
          <p className="text-sm text-[#0D3B4C] mt-1">Tot die tijd toont de homepage tijdelijk standaardfoto's.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="font-[700] text-[#0D3B4C]">Huidige foto's ({items.length}) — sleep om te ordenen</p>
          {items.map((it) => (
            <GalleryRow key={it.id} item={it} busy={busy}
              dragging={draggingId === it.id} dragOver={dragOverId === it.id && draggingId !== it.id}
              onSaveCaption={saveCaption} onDelete={deletePhoto} dnd={dnd} />
          ))}
        </div>
      )}

      {cropState && (
        <CropModal src={cropState.src} caption={caption} uploading={uploading}
          onCancel={() => setCropState(null)} onConfirm={confirmCrop} />
      )}
    </div>
  );
};
